// ---------------------------------------------------------------------------
// Raw-request executor – extracted from ElasticsearchClient so that the
// API-console orchestration (timeout, abort propagation, body normalisation,
// response content-type parsing) lives in its own module.
// ---------------------------------------------------------------------------

import { RETRY_STATUSES, RETRY_DELAYS_MS, addJitter } from "./retryUtils";

/** Timeout applied to every raw request issued from the API console. */
export const RAW_REQUEST_TIMEOUT_MS = 30_000;

// Re-export so existing `import { RETRY_DELAYS_MS } from "./rawRequest"` keeps working.
export { RETRY_DELAYS_MS } from "./retryUtils";

/** Function signature matching `ElasticsearchClient._doFetch`. */
export type DoFetch = (
  url: string,
  headers: Record<string, string>,
  options?: RequestInit & { signal?: AbortSignal },
) => Promise<Response>;

export interface RawRequestError {
  status: number;
  message: string;
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "AbortError" || err.name === "TimeoutError");
}

const RETRYABLE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Signal-aware delay that rejects immediately when the signal fires. */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  const jitteredMs = addJitter(ms);
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(id);
      reject(signal.reason);
    };
    const id = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, jitteredMs);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Execute an arbitrary HTTP request against an Elasticsearch cluster.
 *
 * This is the logic that backs the API-console feature.  It handles
 * timeout / abort propagation, body normalisation, and content-type–aware
 * response parsing.
 */
export async function executeRawRequest(
  doFetch: DoFetch,
  baseUrl: string,
  headers: Record<string, string>,
  method: string,
  path: string,
  body?: string,
  signal?: AbortSignal,
  timeoutMs?: number,
): Promise<{ status: number; body: unknown }> {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path.trim();
  const url = `${normalizedBaseUrl}${trimmedPath.startsWith("/") ? trimmedPath : `/${trimmedPath}`}`;
  // Keep manual timer wiring for fake-timer tests while using AbortSignal.any for composition.
  const effectiveTimeout =
    typeof timeoutMs === "number" &&
    Number.isFinite(timeoutMs) &&
    timeoutMs >= 0 &&
    timeoutMs <= Number.MAX_SAFE_INTEGER
      ? timeoutMs
      : RAW_REQUEST_TIMEOUT_MS;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => {
    timeoutController.abort(new DOMException("Request timeout", "AbortError"));
  }, effectiveTimeout);
  const signals: AbortSignal[] = [timeoutController.signal];
  if (signal) {
    try {
      // Validate signal is acceptable to AbortSignal.any (can reject cross-realm/proxy values)
      AbortSignal.any([signal]);
      signals.push(signal);
    } catch {
      // Ignore invalid signal; use timeout only
    }
  }
  const combinedSignal = AbortSignal.any(signals);
  const rawBody = body && body.trim() ? body : undefined;
  const normalizedMethod = method.toUpperCase();
  const shouldRetryMethod = RETRYABLE_METHODS.has(normalizedMethod);
  try {
    let response: Response | undefined;
    /* eslint-disable no-await-in-loop -- sequential retry with backoff */
    for (let attempt = 0; ; attempt++) {
      try {
        response = await doFetch(
          url,
          { ...headers },
          {
            method: normalizedMethod,
            body: rawBody,
            signal: combinedSignal,
          },
        );

        const isRetryableStatus = RETRY_STATUSES.has(response.status) || response.status >= 500;
        if (!shouldRetryMethod || !isRetryableStatus || attempt >= RETRY_DELAYS_MS.length) {
          break;
        }
      } catch (err: unknown) {
        if (isAbortError(err) || !shouldRetryMethod || attempt >= RETRY_DELAYS_MS.length) {
          throw err;
        }
      }
      await delay(RETRY_DELAYS_MS[attempt] ?? 0, combinedSignal);
    }
    /* eslint-enable no-await-in-loop */
    if (!response) {
      throw new Error("No response received");
    }
    const contentType = (response.headers.get("content-type") ?? "").toLowerCase();
    let responseBody: unknown;
    if (contentType.includes("application/json") || contentType.includes("+json")) {
      responseBody = await response.json().catch((err: unknown) => {
        if (isAbortError(err)) {
          throw err;
        }
        return null;
      });
    } else {
      const text = await response.text().catch((err: unknown) => {
        if (isAbortError(err)) {
          throw err;
        }
        return "";
      });
      responseBody = text || null;
    }
    return { status: response.status, body: responseBody };
  } catch (err: unknown) {
    throw {
      status: 0,
      message: err instanceof Error ? err.message : String(err),
    } satisfies RawRequestError;
  } finally {
    clearTimeout(timeoutId);
  }
}
